import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  CreditCard, ShieldCheck, Package, Truck, Pencil, CheckCircle, Loader2, ArrowLeft, ExternalLink,
  Tag, Percent, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ── Direct Card Payment Form ──────────────────────────────────────── */
const QBOCardForm = ({ orderId, total, billingAddress, onSuccess, onError }) => {
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [card, setCard] = useState({ name: '', number: '', expMonth: '', expYear: '', cvc: '' });
  const [billing, setBilling] = useState({
    street: billingAddress || '',
    city: '',
    state: '',
    zip: '',
  });
  const [showBilling, setShowBilling] = useState(false);
  const updateCard = (field, value) => setCard(prev => ({ ...prev, [field]: value }));
  const updateBilling = (field, value) => setBilling(prev => ({ ...prev, [field]: value }));

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = card.number.replace(/\s/g, '');
    if (!num || num.length < 13) { setErrorMsg('Please enter a valid card number'); return; }
    if (!card.expMonth || !card.expYear) { setErrorMsg('Please enter expiry date'); return; }
    if (!card.cvc || card.cvc.length < 3) { setErrorMsg('Please enter CVC'); return; }
    if (!billing.zip) { setErrorMsg('Please enter billing ZIP code'); return; }

    setProcessing(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('at1s_token');
      const resp = await axios.post(`${API_BASE}/api/qbo/charge`, {
        order_id: orderId,
        amount: total,
        card_number: num,
        card_expiry_month: card.expMonth.padStart(2, '0'),
        card_expiry_year: card.expYear.length === 2 ? `20${card.expYear}` : card.expYear,
        card_cvc: card.cvc,
        card_name: card.name || 'Cardholder',
        billing_street: billing.street,
        billing_city: billing.city,
        billing_state: billing.state,
        billing_zip: billing.zip,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (resp.data.status === 'success') {
        onSuccess(resp.data);
      } else {
        setErrorMsg(resp.data.message || 'Payment is processing. Please wait.');
        setProcessing(false);
      }
    } catch (err) {
      const raw = err.response?.data?.detail || '';
      const detail = typeof raw === 'string' ? raw : JSON.stringify(raw);
      // Show actual QBO error to help diagnose
      setErrorMsg(detail || 'Payment failed. Please check your card details and try again.');
      onError?.(detail);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="qbo-card-form">
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg" data-testid="payment-error">
          {errorMsg}
        </div>
      )}
      <div className="space-y-2.5">
        <Input
          value={card.name}
          onChange={e => updateCard('name', e.target.value)}
          placeholder="Cardholder Name"
          className="h-10"
          data-testid="card-name"
        />
        <div className="relative">
          <Input
            value={card.number}
            onChange={e => updateCard('number', formatCardNumber(e.target.value))}
            placeholder="4111 1111 1111 1111"
            className="h-10 pl-10 font-mono tracking-wider"
            maxLength={19}
            data-testid="card-number"
          />
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input
            value={card.expMonth}
            onChange={e => updateCard('expMonth', e.target.value.replace(/\D/g, '').slice(0, 2))}
            placeholder="MM"
            className="h-10 text-center font-mono"
            maxLength={2}
            data-testid="card-exp-month"
          />
          <Input
            value={card.expYear}
            onChange={e => updateCard('expYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="YYYY"
            className="h-10 text-center font-mono"
            maxLength={4}
            data-testid="card-exp-year"
          />
          <Input
            value={card.cvc}
            onChange={e => updateCard('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="CVC"
            className="h-10 text-center font-mono"
            maxLength={4}
            type="password"
            data-testid="card-cvc"
          />
        </div>

        {/* Billing Address */}
        <div className="border-t pt-2.5 mt-1">
          <p className="text-xs font-semibold text-slate-500 mb-2">Billing Address</p>
          <div className="space-y-2">
            <Input
              value={billing.street}
              onChange={e => updateBilling('street', e.target.value)}
              placeholder="Street address"
              className="h-9 text-sm"
              data-testid="billing-street"
            />
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={billing.city}
                onChange={e => updateBilling('city', e.target.value)}
                placeholder="City"
                className="h-9 text-sm"
                data-testid="billing-city"
              />
              <Input
                value={billing.state}
                onChange={e => updateBilling('state', e.target.value)}
                placeholder="State"
                className="h-9 text-sm"
                maxLength={2}
                data-testid="billing-state"
              />
              <Input
                value={billing.zip}
                onChange={e => updateBilling('zip', e.target.value)}
                placeholder="ZIP *"
                className="h-9 text-sm"
                maxLength={10}
                data-testid="billing-zip"
              />
            </div>
          </div>
        </div>
      </div>
      <Button
        type="submit"
        disabled={processing}
        className="w-full h-14 text-base font-bold bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 rounded-xl shadow-lg"
        data-testid="pay-now-btn"
      >
        {processing ? (
          <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Processing Payment...</>
        ) : (
          <><CreditCard className="h-5 w-5 mr-2" /> Pay ${total.toFixed(2)}</>
        )}
      </Button>

      {/* Powered by QuickBooks + accepted methods */}
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>Powered by <strong className="text-slate-500">QuickBooks Payments</strong></span>
        </div>
        <div className="flex items-center justify-center gap-3 opacity-60">
          <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Visa"><rect width="38" height="24" rx="3" fill="#1434CB"/><text x="19" y="15" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="sans-serif">VISA</text></svg>
          <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Mastercard"><rect width="38" height="24" rx="3" fill="#252525"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B"/></svg>
          <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Amex"><rect width="38" height="24" rx="3" fill="#2E77BC"/><text x="19" y="15" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="sans-serif">AMEX</text></svg>
          <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Apple Pay"><rect width="38" height="24" rx="3" fill="#000"/><text x="19" y="15" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="sans-serif">Pay</text></svg>
        </div>
      </div>
    </form>
  );
};


/* ── Review & Pay Page ─────────────────────────────────────────────── */
export const ReviewAndPay = ({
  orderItems, user, shippingAddress, isSubscription, recurrence,
  subtotal, customerDiscountPct, discount, afterDiscount, shipping, tax, total,
  totalFilters, deliveryDate, totalDiscountPct,
  createdOrderId,
  paymentSuccess, onPaymentSuccess, onPaymentError,
  onBack, onAddressChange,
  promoResult, promoDiscount,
}) => {
  const SUBSCRIPTION_DISCOUNT = 0;
  const [editingAddress, setEditingAddress] = useState(false);
  const [tempAddr, setTempAddr] = useState(shippingAddress || '');

  const handleSaveAddress = () => {
    if (tempAddr.trim() && onAddressChange) {
      onAddressChange(tempAddr.trim());
    }
    setEditingAddress(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 space-y-5">
        {/* Back Button */}
        {onBack && !paymentSuccess && (
          <Button variant="outline" onClick={onBack} className="mb-1" data-testid="review-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Shipping
          </Button>
        )}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="bg-slate-100 rounded-t-xl px-5 py-3">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Package className="h-4 w-4 text-sky-600" /> Order Items</h2>
          </div>
          <div className="divide-y">
            {orderItems.map((item, i) => (
              <div key={`${item.size}-${item.merv}-${i}`} className="px-5 py-3 flex justify-between items-center" data-testid={`review-item-${i}`}>
                <div>
                  <p className="font-medium text-slate-800">{item.size}</p>
                  <p className="text-xs text-slate-500">MERV {item.merv} &middot; Qty: {item.quantity} &middot; ${item.price.toFixed(2)} each</p>
                </div>
                <p className="font-bold text-slate-800">${(item.price * parseInt(item.quantity || 0)).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Truck className="h-4 w-4 text-sky-600" /> Ship To</h3>
            {!editingAddress && !paymentSuccess && (
              <Button variant="ghost" size="sm" onClick={() => { setTempAddr(shippingAddress); setEditingAddress(true); }} className="text-sky-600 hover:text-sky-700 text-xs h-7 px-2" data-testid="edit-address-btn">
                <Pencil className="h-3 w-3 mr-1" /> Edit
              </Button>
            )}
          </div>
          <p className="text-sm text-slate-700 font-medium">{user?.name}</p>
          {editingAddress ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={tempAddr}
                onChange={e => setTempAddr(e.target.value)}
                className="w-full border rounded-lg p-2.5 text-sm text-slate-700 focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none resize-none"
                rows={3}
                data-testid="edit-address-input"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveAddress} data-testid="save-address-btn">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditingAddress(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-1">{shippingAddress || 'No address provided'}</p>
          )}
          {deliveryDate && !editingAddress && (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <Truck className="h-3 w-3" /> Est. delivery by {deliveryDate instanceof Date ? deliveryDate.toLocaleDateString() : deliveryDate}
            </p>
          )}
        </div>
      </div>

      {/* Payment Summary */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border shadow-sm p-5 sticky top-4">
          <h3 className="font-bold text-slate-800 mb-4">Order Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{totalFilters} filter{totalFilters !== 1 ? 's' : ''}</span>
              <span className="font-semibold text-slate-700" data-testid="subtotal">${subtotal.toFixed(2)}</span>
            </div>
            {isSubscription && SUBSCRIPTION_DISCOUNT > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Subscription ({SUBSCRIPTION_DISCOUNT}% off)</span>
                <span>-${(subtotal * SUBSCRIPTION_DISCOUNT / 100).toFixed(2)}</span>
              </div>
            )}
            {customerDiscountPct > 0 && (
              <div className="flex justify-between text-sm text-blue-600">
                <span className="flex items-center gap-1"><Percent className="h-3 w-3" /> Volume Discount ({customerDiscountPct}%)</span>
                <span>-${discount.toFixed(2)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="flex justify-between text-sm text-purple-600">
                <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> Promo ({promoResult?.code})</span>
                <span>-${promoDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 flex items-center gap-1"><Truck className="h-3 w-3" /> Shipping</span>
              {shipping === 0 ? <span className="text-green-600">FREE</span> : <span className="text-slate-700">${shipping.toFixed(2)}</span>}
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Tax (8.25%)</span>
              <span className="font-semibold text-slate-700" data-testid="tax">${tax.toFixed(2)}</span>
            </div>
            {subtotal > 0 && afterDiscount < 100 && afterDiscount > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg" data-testid="free-shipping-hint">
                Add ${(100 - afterDiscount).toFixed(2)} more for free shipping!
              </p>
            )}
            <div className="border-t pt-3 flex justify-between items-center">
              <span className="text-lg font-bold text-slate-800">Total</span>
              <span className="text-2xl font-bold text-sky-700" data-testid="total">${total.toFixed(2)}</span>
            </div>

            {/* Payment */}
            {paymentSuccess ? (
              <div className="mt-4 text-center py-6" data-testid="payment-success-inline">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-bold text-green-700">Payment Successful!</p>
                <p className="text-sm text-slate-500 mt-1">Redirecting to confirmation...</p>
              </div>
            ) : createdOrderId ? (
              <div className="mt-4">
                <QBOCardForm orderId={createdOrderId} total={total} billingAddress={shippingAddress} onSuccess={onPaymentSuccess} onError={onPaymentError} />
              </div>
            ) : (
              <div className="mt-4 text-center py-6">
                <Loader2 className="h-8 w-8 text-sky-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-500">Preparing order...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
